/**
 * Composable for managing the PixiJS map renderer
 */

import { ref, shallowRef, watch, onMounted, onUnmounted, Ref } from 'vue';
import { MapRenderer } from '@/pixi/MapRenderer';
import { useMapStore } from '@/stores/map';
import { useEditorStore, EditorTool } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';

export function useMapRenderer(containerRef: Ref<HTMLElement | null>) {
    const mapStore = useMapStore();
    const editorStore = useEditorStore();
    const assetsStore = useAssetsStore();

    const renderer = shallowRef<MapRenderer | null>(null);
    const isInitialized = ref(false);

    // Initialize renderer when container is available
    async function initRenderer(): Promise<void> {
        if (!containerRef.value || isInitialized.value) return;

        renderer.value = new MapRenderer({
            container: containerRef.value,
            onTileHover: handleTileHover,
            onTileClick: handleTileClick,
            onDragStart: handleDragStart,
            onDrag: handleDrag,
            onDragEnd: handleDragEnd,
            onZoom: handleZoom,
            onPan: handlePan,
        });

        await renderer.value.init();
        isInitialized.value = true;

        // If map is already loaded, render it
        if (mapStore.hasMap) {
            renderMap();
        }
    }

    // Render all tiles (culling disabled for testing)
    function renderAllTiles(): void {
        if (!renderer.value || !mapStore.document) return;

        const { width, height, tiles } = mapStore.document;

        try {
            for (let x = 0; x < width; x++) {
                for (let y = 0; y < height; y++) {
                    const tile = tiles[x]?.[y];
                    if (tile) {
                        renderTileAt(x, y, tile);
                    }
                }
            }
        } catch (e) {
            console.warn('Error rendering tiles:', e);
        }
    }

    // Initialize map - render all tiles
    function renderMap(): void {
        if (!renderer.value || !mapStore.document) return;

        const { width, height } = mapStore.document;

        renderer.value.setMapSize(width, height);
        renderer.value.centerOn(width / 2, height / 2);

        // Render all tiles and bake to texture
        renderAllTiles();
        renderer.value.bakeToTexture();

        // Update property overlays
        updatePropertyOverlay();
    }

    // Render a single tile at position
    function renderTileAt(x: number, y: number, tile: { tileSprite: number; tileSpriteFrame: number; objectSprite: number; objectSpriteFrame: number }): void {
        if (!renderer.value) return;

        // Render base tile (sprite ID 0 is valid, use >= 0)
        const tileTexture = assetsStore.getTileTexture(tile.tileSprite, tile.tileSpriteFrame);
        renderer.value.setTileSprite(x, y, tileTexture || null);

        // Render object (sprite ID 0 means no object)
        if (tile.objectSprite > 0) {
            const objectTexture = assetsStore.getTileTexture(tile.objectSprite, tile.objectSpriteFrame);
            const entry = assetsStore.getAtlasEntry(tile.objectSprite, tile.objectSpriteFrame);
            const pivotX = entry?.pivot?.x ?? 0;
            const pivotY = entry?.pivot?.y ?? 0;
            renderer.value.setObjectSprite(x, y, objectTexture || null, pivotX, pivotY);
        } else {
            renderer.value.setObjectSprite(x, y, null);
        }
    }

    // Throttle cursor updates to avoid excessive redraws
    let lastCursorX = -1;
    let lastCursorY = -1;

    // Handle tile hover
    function handleTileHover(x: number, y: number): void {
        if (!mapStore.document) return;

        const { width, height } = mapStore.document;
        if (x >= 0 && x < width && y >= 0 && y < height) {
            editorStore.updateMousePosition(0, 0, x, y);

            // Only update cursor if tile position changed
            if (x !== lastCursorX || y !== lastCursorY) {
                lastCursorX = x;
                lastCursorY = y;

                // Get brush tiles based on current tool, including textures for preview
                type BrushTileInfo = {
                    offsetX: number;
                    offsetY: number;
                    texture?: ReturnType<typeof assetsStore.getTileTexture>;
                    pivotX?: number;
                    pivotY?: number;
                };
                let brushTiles: BrushTileInfo[] = [];

                if (editorStore.currentTool === EditorTool.PAINT_TILE && editorStore.tileBrush.length > 0) {
                    brushTiles = editorStore.tileBrush.map(t => ({
                        offsetX: t.offsetX,
                        offsetY: t.offsetY,
                        texture: assetsStore.getTileTexture(t.spriteId, t.frame),
                    }));
                } else if (editorStore.currentTool === EditorTool.PAINT_OBJECT && editorStore.objectBrush.length > 0) {
                    brushTiles = editorStore.objectBrush.map(t => {
                        const entry = assetsStore.getAtlasEntry(t.spriteId, t.frame);
                        return {
                            offsetX: t.offsetX,
                            offsetY: t.offsetY,
                            texture: assetsStore.getTileTexture(t.spriteId, t.frame),
                            pivotX: entry?.pivot?.x ?? 0,
                            pivotY: entry?.pivot?.y ?? 0,
                        };
                    });
                }

                renderer.value?.updateCursor(x, y, brushTiles);
            }
        }
    }

    // Handle tile click
    function handleTileClick(x: number, y: number, button: number): void {
        if (!mapStore.document) return;

        const { width, height } = mapStore.document;
        if (x < 0 || x >= width || y < 0 || y >= height) return;

        // Right-click to eyedrop
        if (button === 2) {
            const tile = mapStore.getTile(x, y);
            if (tile) {
                if (tile.objectSprite > 0) {
                    editorStore.selectObject(tile.objectSprite, tile.objectSpriteFrame);
                } else {
                    editorStore.selectTile(tile.tileSprite, tile.tileSpriteFrame);
                }
            }
            return;
        }

        // Left-click to apply tool
        applyToolAt(x, y);
    }

    // Handle drag start
    function handleDragStart(x: number, y: number): void {
        mapStore.beginOperation();
        applyToolAt(x, y);
    }

    // Handle drag
    function handleDrag(x: number, y: number): void {
        applyToolAt(x, y);
    }

    // Handle drag end
    function handleDragEnd(_x: number, _y: number): void {
        mapStore.endOperation();
        // Update property overlays in case tile sprites changed (affects water detection)
        updatePropertyOverlay();
    }

    // Handle zoom
    function handleZoom(zoom: number): void {
        editorStore.setZoom(zoom);
    }

    // Flag to prevent feedback loops between renderer and store
    let updatingFromRenderer = false;

    // Handle pan
    function handlePan(newPanX: number, newPanY: number): void {
        updatingFromRenderer = true;
        editorStore.panX = newPanX;
        editorStore.panY = newPanY;
        updatingFromRenderer = false;
    }

    // Apply current tool at position
    function applyToolAt(centerX: number, centerY: number): void {
        if (!mapStore.document) return;

        const tool = editorStore.currentTool;

        switch (tool) {
            case EditorTool.PAINT_TILE:
                paintWithMultiBrush(centerX, centerY, false);
                break;

            case EditorTool.PAINT_OBJECT:
                paintWithMultiBrush(centerX, centerY, true);
                break;

            case EditorTool.ERASE_OBJECT:
                mapStore.clearObject(centerX, centerY);
                rerenderTileAt(centerX, centerY);
                break;

            case EditorTool.TOGGLE_BLOCKED:
                const currentTile = mapStore.getTile(centerX, centerY);
                if (currentTile) {
                    mapStore.setTileFlags(centerX, centerY, { blocked: !currentTile.blocked });
                    updatePropertyOverlay();
                }
                break;

            case EditorTool.TOGGLE_TELEPORT:
                const tile = mapStore.getTile(centerX, centerY);
                if (tile) {
                    mapStore.setTileFlags(centerX, centerY, { teleport: !tile.teleport });
                    updatePropertyOverlay();
                }
                break;
        }
    }

    // Paint with multi-tile brush
    function paintWithMultiBrush(centerX: number, centerY: number, isObject: boolean): void {
        const brush = isObject ? editorStore.objectBrush : editorStore.tileBrush;

        if (brush.length === 0) {
            // Single tile mode - fall back to selected sprite
            if (isObject) {
                mapStore.setObject(centerX, centerY, editorStore.selectedObjectSprite, editorStore.selectedObjectSpriteFrame);
            } else {
                mapStore.setBaseTile(centerX, centerY, editorStore.selectedTileSprite, editorStore.selectedTileSpriteFrame);
            }
            // Re-render single tile
            const updatedTile = mapStore.getTile(centerX, centerY);
            if (updatedTile) {
                renderTileAt(centerX, centerY, updatedTile);
            }
            return;
        }

        // Paint all tiles in the brush
        for (const tile of brush) {
            const destX = centerX + tile.offsetX;
            const destY = centerY + tile.offsetY;

            // Check bounds
            if (destX < 0 || destX >= mapStore.mapWidth ||
                destY < 0 || destY >= mapStore.mapHeight) {
                continue;
            }

            if (isObject) {
                mapStore.setObject(destX, destY, tile.spriteId, tile.frame);
            } else {
                mapStore.setBaseTile(destX, destY, tile.spriteId, tile.frame);
            }

            // Re-render the tile
            const updatedTile = mapStore.getTile(destX, destY);
            if (updatedTile) {
                renderTileAt(destX, destY, updatedTile);
            }
        }
    }

    // Helper to re-render a tile after modification
    function rerenderTileAt(x: number, y: number): void {
        const tile = mapStore.getTile(x, y);
        if (tile) {
            renderTileAt(x, y, tile);
        }
    }

    // Watch for map changes - only re-render when a NEW map is loaded
    // Individual tile edits are handled by the paint functions directly
    let lastMapKey: string | null = null;
    watch(() => mapStore.document, (doc) => {
        if (isInitialized.value && mapStore.hasMap && doc) {
            // Create a key that changes when a different map is loaded
            const mapKey = `${doc.filename}:${doc.width}x${doc.height}`;
            if (mapKey !== lastMapKey) {
                lastMapKey = mapKey;
                renderMap();
            }
        } else {
            lastMapKey = null;
        }
    });

    // Re-render tiles when assets load (debounced to batch spritesheet loads)
    let assetRenderTimeout: ReturnType<typeof setTimeout> | null = null;
    function debouncedRerenderAll(): void {
        if (assetRenderTimeout) {
            clearTimeout(assetRenderTimeout);
        }
        assetRenderTimeout = setTimeout(() => {
            assetRenderTimeout = null;
            if (isInitialized.value && mapStore.hasMap && renderer.value) {
                renderAllTiles();
                renderer.value.bakeToTexture();
            }
        }, 100);
    }

    // Watch for assets to finish loading (initial load)
    watch(() => assetsStore.isLoaded, (loaded) => {
        if (loaded && isInitialized.value && mapStore.hasMap) {
            debouncedRerenderAll();
        }
    });

    // Watch for new sprites being loaded (dynamic loading)
    watch(() => assetsStore.loadedSpritesheets.size, () => {
        if (isInitialized.value && mapStore.hasMap && !assetsStore.loading) {
            debouncedRerenderAll();
        }
    });

    // Watch for view settings
    watch(() => editorStore.showGrid, (show) => {
        renderer.value?.setShowGrid(show);
    });

    watch(() => editorStore.showObjects, (show) => {
        renderer.value?.setShowObjects(show);
    });

    // Update property overlays
    function updatePropertyOverlay(): void {
        if (!renderer.value || !mapStore.document) return;
        renderer.value.updatePropertyOverlay(
            mapStore.document.tiles,
            editorStore.showBlocked,
            editorStore.showTeleports,
            editorStore.showFarming,
            editorStore.showBuildable,
            editorStore.showWater
        );
    }

    // Watch for property visibility toggles
    watch(() => editorStore.showBlocked, updatePropertyOverlay);
    watch(() => editorStore.showTeleports, updatePropertyOverlay);
    watch(() => editorStore.showFarming, updatePropertyOverlay);
    watch(() => editorStore.showBuildable, updatePropertyOverlay);
    watch(() => editorStore.showWater, updatePropertyOverlay);

    // Watch for external pan changes (e.g., from minimap click)
    watch(() => editorStore.panX, (panX) => {
        if (!updatingFromRenderer && renderer.value) {
            renderer.value.panX = panX;
        }
    });

    watch(() => editorStore.panY, (panY) => {
        if (!updatingFromRenderer && renderer.value) {
            renderer.value.panY = panY;
        }
    });

    // Lifecycle
    onMounted(async () => {
        await initRenderer();
    });

    onUnmounted(() => {
        if (assetRenderTimeout) {
            clearTimeout(assetRenderTimeout);
            assetRenderTimeout = null;
        }
        renderer.value?.destroy();
        renderer.value = null;
    });

    return {
        renderer,
        isInitialized,
        renderMap,
    };
}
